import { GoogleMap, MarkerF, useJsApiLoader } from "@react-google-maps/api";
import PlacesAutoComplete from "./PlacesAutocomplete";
import { useEffect, useState, useCallback } from "react";
import usePlacesAutocomplete from "use-places-autocomplete";

const libraries = ["places"];

const extractCity = (addressComponents) => {
  if (!addressComponents) return null;

  // Try different component types that might contain the city
  const cityTypes = [
    'locality',                    // City/Town
    'administrative_area_level_2', // County (fallback for smaller places)
    'administrative_area_level_1', // State (last resort)
    'sublocality_level_1',        // District/Neighborhood in some countries
    'political'                   // Political entity
  ];

  for (const type of cityTypes) {
    const component = addressComponents.find(comp =>
      comp.types.includes(type)
    );
    if (component) {
      return component.long_name;
    }
  }

  return null;
}

const Map = ({ setSelectedLocation, selectedLocation, setPlaceId, setLatlng, googleCity, setGoogleCity, disabled, setAddLocationClicked, setLinkHowToReach, formData }) => {

  const [mapRef, setMapRef] = useState(null);
  const [markerPosition, setMarkerPosition] = useState({
    lat: 19.0760,
    lng: 72.8777,
  });

  // Load Google Maps API
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: 'AIzaSyBJQg3RkKR9QCBuyWNOKPJSibgs4rOxso8', // Consider moving to env variable
    libraries: libraries,
  });

  // Places Autocomplete hook
  const {
    ready,
    value,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    requestOptions: {
      componentRestrictions: { country: "in" },
      // You can add location bias here if needed
      // location: new google.maps.LatLng(lat, lng),
      // radius: 200 * 1000, // 200km radius
    },
    debounce: 300, // Increased debounce for better performance
  });

  // Geocode address to get coordinates and place details
  const geocodeAddress = useCallback(() => {
    if (!selectedLocation || !isLoaded || !window.google?.maps) return;

    const geocoder = new window.google.maps.Geocoder();
    if (formData.google_location_latitude) {
      geocoder.geocode({ location: { lat: parseFloat(formData['google_location_latitude']), lng: parseFloat(formData['google_location_longitude']) }}, (results, status) => {
        if (status === 'OK' && results?.[0]) {
          const result = results[0];
          const lat = result.geometry.location.lat();
          const lng = result.geometry.location.lng();
          // Update all state consistently
          const newPosition = { lat, lng };
          setMarkerPosition(newPosition);
          console.log(formData.google_location_place_id, result.place_id)
          if (!formData.google_location_place_id || formData.google_location_place_id != result.place_id) {
            console.log("here")
            setPlaceId(result.place_id);
            // setLinkHowToReach(`https://www.google.com/maps/place/?q=place_id:${result.place_id}`)
            setLatlng({ lat, lng }); // Fixed: using consistent naming
            setGoogleCity(extractCity(result.address_components));
          }
        } else {
          console.error('Geocoding failed:', status, formData);
        }
      });
    }
  }, [selectedLocation, isLoaded, setPlaceId, setLatlng, formData.google_location_latitude, formData.google_location_place_id]);

  // Reverse geocode coordinates to get address and place details
  const reverseGeocode = useCallback((lat, lng, preserveExactPosition = false) => {
    if (!window?.google?.maps) return;

    const geocoder = new window.google.maps.Geocoder();
    const latlng = { lat: lat, lng: lng };

    geocoder.geocode({ location: latlng }, (results, status) => {
      if (status === "OK" && results?.[0]) {
        const result = results[0];

        // Use the exact dragged position instead of the geocoded position
        const finalLat = preserveExactPosition ? lat : result.geometry.location.lat();
        const finalLng = preserveExactPosition ? lng : result.geometry.location.lng();

        // Update all state consistently
        setPlaceId(result.place_id);
        setLatlng({ lat: finalLat, lng: finalLng });
        setSelectedLocation(result.formatted_address);
        setMarkerPosition({ lat: finalLat, lng: finalLng });

        // IMPORTANT: Don't update marker position when preserving exact position
        // The marker should stay where the user dragged it
        // if (!preserveExactPosition) {
        //   setMarkerPosition({ lat: finalLat, lng: finalLng });
        // }

      } else {
        console.error('Reverse geocoding failed:', status);
      }
    });
  }, [setPlaceId, setLatlng, setSelectedLocation]);

  // Effect to geocode when selectedLocation changes
  useEffect(() => {
    geocodeAddress();
  }, [geocodeAddress]);

  // Map load handler
  const onLoad = useCallback((map) => {
    setMapRef(map);
  }, []);

  // Map unload handler
  const onUnmount = useCallback(() => {
    setMapRef(null);
  }, []);

  // Handle marker click
  const handleMarkerClick = useCallback((lat, lng) => {
    if (mapRef) {
      mapRef.setZoom(20);
      mapRef.panTo({ lat, lng });
    }
  }, [mapRef]);

  // Handle marker drag end
  const handleMarkerDragEnd = useCallback((e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();

    // Update marker position immediately and keep it there
    const newPosition = { lat, lng };
    setMarkerPosition(newPosition);

    // Get address and place ID without moving the marker
    reverseGeocode(lat, lng, true);
  }, [reverseGeocode]);

  // Handle loading error
  if (loadError) {
    return <div className="text-red-500">Error loading Google Maps</div>;
  }

  // Handle loading state
  if (!isLoaded) {
    return <div className="text-gray-500">Loading Google Maps...</div>;
  }

  return (
    <div className="Map mt-4">
      {/* Show autocomplete only when no location is selected */}
      {!disabled && (
        <PlacesAutoComplete
          setMarkerPosition={setMarkerPosition}
          setSelectedLocation={setSelectedLocation}
          ready={ready}
          value={value}
          status={status}
          data={data}
          setValue={setValue}
          clearSuggestions={clearSuggestions}
          setPlaceId={setPlaceId}
          setGoogleCity={setGoogleCity}
          setLatlng={setLatlng}
        />
      )}

      {/* Google Map */}
      <GoogleMap
        mapContainerClassName="w-full h-[300px] rounded-xl"
        center={markerPosition}
        zoom={20} // Reduced from 20 for better initial view
        onLoad={onLoad}
        onUnmount={onUnmount}
        onClick={() => setAddLocationClicked?.(true)}
        options={{

          // Additional map options for better UX
          // zoomControl: true,
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: true,
          gestureHandling: disabled ? 'none' : 'auto',
          // disableDefaultUI: true,
          zoomControl: !disabled
        }}
      >
        <MarkerF
          position={markerPosition}
          draggable={disabled ? false : true}
          onClick={() => handleMarkerClick(markerPosition.lat, markerPosition.lng)}
          onDragEnd={handleMarkerDragEnd}
          options={{
            // Custom marker options
            animation: window.google?.maps?.Animation?.DROP,
          }}
        />
      </GoogleMap>

      {/* Debug info (remove in production) */}

    </div>
  );
};

export default Map;